import { initializeSentryConfig } from '@mb/common/client';

/** Workforce Vite env reads — static `import.meta.env.*` only (Vite does not inline dynamic access). */

const truthyValues = new Set(['1', 'true', 'yes', 'on']);

function isTruthy(value: string | undefined): boolean {
  if (value === undefined || value === '') return false;
  return truthyValues.has(value.trim().toLowerCase());
}

export function readWorkforceBuildVersion(): string | undefined {
  const fromVite = import.meta.env['VITE_BUILD_VERSION'];
  if (fromVite !== undefined && String(fromVite).trim() !== '') {
    return String(fromVite).trim();
  }

  if (typeof window !== 'undefined') {
    const fromWindow = (window as { __ENV__?: Record<string, unknown> }).__ENV__?.['BUILD_VERSION'];
    if (fromWindow !== undefined && String(fromWindow).trim() !== '') {
      return String(fromWindow).trim();
    }
  }

  return undefined;
}

export function readWorkforceSentryRelease(): string {
  const fromVite = import.meta.env['VITE_NODE_ENV'];
  if (fromVite !== undefined && String(fromVite).trim() !== '') {
    return String(fromVite).trim();
  }

  if (typeof window !== 'undefined') {
    const fromWindow = (window as { __ENV__?: Record<string, unknown> }).__ENV__?.['NODE_ENV'];
    if (fromWindow !== undefined && String(fromWindow).trim() !== '') {
      return String(fromWindow).trim();
    }
  }

  return 'development';
}

export function readWorkforceSentryDsn(): string | undefined {
  const fromVite = import.meta.env.SENTRY_DSN ?? import.meta.env.VITE_SENTRY_DSN;
  if (fromVite !== undefined && String(fromVite).trim() !== '') {
    return String(fromVite).trim();
  }

  if (typeof window !== 'undefined') {
    const fromWindow = (window as { __ENV__?: Record<string, unknown> }).__ENV__?.['SENTRY_DSN'];
    if (fromWindow !== undefined && String(fromWindow).trim() !== '') {
      return String(fromWindow).trim();
    }
  }

  return undefined;
}

export function readWorkforceSentryDisabled(): boolean {
  if (typeof window !== 'undefined') {
    const fromWindow = (window as { __ENV__?: Record<string, unknown> }).__ENV__?.['SENTRY_DISABLED'];
    if (fromWindow !== undefined && fromWindow !== '') {
      return isTruthy(String(fromWindow));
    }
  }

  const raw = import.meta.env.SENTRY_DISABLED ?? import.meta.env.VITE_SENTRY_DISABLED;
  return isTruthy(raw);
}

export function isWorkforceSentryEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(readWorkforceSentryDsn()) && !readWorkforceSentryDisabled();
}

/**
 * Call once in `main.ts` before `bootstrapApplication` (same as Sentry’s Angular guide).
 * @see https://docs.sentry.io/platforms/javascript/guides/angular/
 */
export function initWorkforceSentry(): void {
  if (!isWorkforceSentryEnabled()) {
    return;
  }

  const dsn = readWorkforceSentryDsn();
  if (!dsn) {
    return;
  }

  initializeSentryConfig({
    dsn,
    release: readWorkforceSentryRelease(),
    // Same-origin proxy — avoids ad-blockers and bogus CORS errors on ingest.de.sentry.io
    tunnel: '/api/sentry-tunnel',
  });
}
